import { useCallback, useMemo, useState } from 'react'
import { filterTableList, sortTableList, type SortDirection } from '../utils/table_list'

export type { SortDirection }

export interface TableQueryState<T extends object> {
  searchTerm: string
  entriesPerPage: number
  currentPage: number
  sortField: keyof T | null
  sortDirection: SortDirection
  filteredItems: T[]
  paginatedItems: T[]
  totalPages: number
  startIndex: number
  endIndex: number
}

export interface TableQueryActions<T extends object> {
  setSearch: (term: string) => void
  setPageSize: (size: number) => void
  changePage: (page: number) => void
  goToPreviousPage: () => void
  goToNextPage: () => void
  handleSort: (field: keyof T) => void
}

export interface UseTableQueryStateOptions<T extends object> {
  items: T[]
  defaultPageSize?: number
  matchesSearch: (item: T, term: string) => boolean
}

export function useTableQueryState<T extends object>({
  items,
  defaultPageSize = 10,
  matchesSearch,
}: UseTableQueryStateOptions<T>): {
  table: TableQueryState<T>
  actions: TableQueryActions<T>
} {
  const [searchTerm, setSearchTerm] = useState('')
  const [entriesPerPage, setEntriesPerPage] = useState(defaultPageSize)
  const [currentPage, setCurrentPage] = useState(1)
  const [sortField, setSortField] = useState<keyof T | null>(null)
  const [sortDirection, setSortDirection] = useState<SortDirection>('asc')

  const filteredItems = useMemo(() => {
    const filtered = filterTableList(items, searchTerm, matchesSearch)
    return sortTableList(filtered, sortField, sortDirection)
  }, [items, searchTerm, sortField, sortDirection, matchesSearch])

  const totalPages = Math.ceil(filteredItems.length / entriesPerPage) || 1
  const startIndex = (currentPage - 1) * entriesPerPage
  const endIndex = startIndex + entriesPerPage
  const paginatedItems = filteredItems.slice(startIndex, endIndex)

  const handleSort = useCallback(
    (field: keyof T) => {
      if (sortField === field) {
        setSortDirection(prev => (prev === 'asc' ? 'desc' : 'asc'))
      } else {
        setSortField(field)
        setSortDirection('asc')
      }
      setCurrentPage(1)
    },
    [sortField]
  )

  const setSearch = useCallback((term: string) => {
    setSearchTerm(term)
    setCurrentPage(1)
  }, [])

  const setPageSize = useCallback((size: number) => {
    setEntriesPerPage(size)
    setCurrentPage(1)
  }, [])

  const changePage = useCallback(
    (page: number) => setCurrentPage(Math.min(Math.max(1, page), totalPages)),
    [totalPages]
  )

  const goToPreviousPage = useCallback(() => {
    setCurrentPage(prev => Math.max(1, prev - 1))
  }, [])

  const goToNextPage = useCallback(() => {
    setCurrentPage(prev => Math.min(totalPages, prev + 1))
  }, [totalPages])

  return {
    table: {
      searchTerm,
      entriesPerPage,
      currentPage,
      sortField,
      sortDirection,
      filteredItems,
      paginatedItems,
      totalPages,
      startIndex,
      endIndex,
    },
    actions: {
      setSearch,
      setPageSize,
      changePage,
      goToPreviousPage,
      goToNextPage,
      handleSort,
    },
  }
}
